/**
 * Google SERP Pixel Width Measurement & Truncation Utilities
 *
 * Google Desktop SERP font specs:
 * - Title: Arial, 20px (historically 18px), regular weight. User requirement: max 512 px.
 * - Snippet Description: Arial, 14px (or 13px), regular weight. User requirement: max 970 px.
 *
 * Character metrics are based on standard TrueType/PostScript Arial proportional width tables (units per 1000).
 */

const ARIAL_CHAR_WIDTHS_1000 = {
  " ": 278,
  "!": 278,
  '"': 355,
  "#": 556,
  "$": 556,
  "%": 889,
  "&": 667,
  "'": 191,
  "(": 333,
  ")": 333,
  "*": 389,
  "+": 584,
  ",": 278,
  "-": 333,
  ".": 278,
  "/": 278,
  "0": 556,
  "1": 556,
  "2": 556,
  "3": 556,
  "4": 556,
  "5": 556,
  "6": 556,
  "7": 556,
  "8": 556,
  "9": 556,
  ":": 278,
  ";": 278,
  "<": 584,
  "=": 584,
  ">": 584,
  "?": 556,
  "@": 1015,
  "A": 667,
  "B": 667,
  "C": 722,
  "D": 722,
  "E": 667,
  "F": 611,
  "G": 778,
  "H": 722,
  "I": 278,
  "J": 500,
  "K": 667,
  "L": 556,
  "M": 833,
  "N": 722,
  "O": 778,
  "P": 667,
  "Q": 778,
  "R": 722,
  "S": 667,
  "T": 611,
  "U": 722,
  "V": 667,
  "W": 944,
  "X": 667,
  "Y": 667,
  "Z": 611,
  "[": 278,
  "\\": 278,
  "]": 278,
  "^": 469,
  "_": 556,
  "`": 333,
  "a": 556,
  "b": 556,
  "c": 500,
  "d": 556,
  "e": 556,
  "f": 278,
  "g": 556,
  "h": 556,
  "i": 222,
  "j": 222,
  "k": 500,
  "l": 222,
  "m": 833,
  "n": 556,
  "o": 556,
  "p": 556,
  "q": 556,
  "r": 333,
  "s": 500,
  "t": 278,
  "u": 556,
  "v": 500,
  "w": 722,
  "x": 500,
  "y": 500,
  "z": 500,
  "{": 334,
  "|": 260,
  "}": 334,
  "~": 584,
  // Danish & accented characters
  "æ": 889,
  "ø": 611,
  "å": 556,
  "Æ": 1000,
  "Ø": 778,
  "Å": 667,
  "é": 556,
  "è": 556,
  "ö": 556,
  "ä": 556,
  "ü": 556,
  "Ö": 778,
  "Ä": 667,
  "Ü": 722,
  // Typographic marks
  "–": 556, // en-dash
  "—": 1000, // em-dash
  "’": 278,
  "‘": 278,
  "“": 355,
  "”": 355,
  "…": 1000,
  "•": 500,
  "·": 278,
};

const DEFAULT_CHAR_WIDTH_1000 = 556;

/**
 * Calculate the pixel width of a string in Arial at a specific font size.
 * @param {string} text
 * @param {number} fontSize (e.g. 20 for title, 14 for description)
 * @returns {number} width in pixels (rounded to 1 decimal place)
 */
export function getPixelWidth(text, fontSize = 20) {
  if (!text || typeof text !== "string") return 0;
  let totalWidth1000 = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const width = ARIAL_CHAR_WIDTHS_1000[ch] !== undefined ? ARIAL_CHAR_WIDTHS_1000[ch] : DEFAULT_CHAR_WIDTH_1000;
    totalWidth1000 += width;
  }
  return (totalWidth1000 / 1000) * fontSize;
}

/**
 * Ensure a title fits strictly within maxPx (default 512px).
 * Uses Arial 20px font size.
 */
export function ensureTitleLimit(title, maxPx = 512) {
  if (!title || typeof title !== "string") return "";
  const cleanTitle = title.trim().replace(/\s+/g, " ");
  const currentPx = getPixelWidth(cleanTitle, 20);
  if (currentPx <= maxPx) return cleanTitle;

  // If title has a brand suffix like " | CPH Collective", prioritize keeping that
  const brandSuffix = " | CPH Collective";
  const brandWidth = getPixelWidth(brandSuffix, 20);

  if (cleanTitle.includes(brandSuffix) && maxPx > brandWidth + 50) {
    const mainPart = cleanTitle.replace(brandSuffix, "").trim();
    const availableForMain = maxPx - brandWidth;
    let trimmedMain = "";
    const words = mainPart.split(" ");
    for (const word of words) {
      const candidate = trimmedMain ? `${trimmedMain} ${word}` : word;
      if (getPixelWidth(candidate, 20) <= availableForMain) {
        trimmedMain = candidate;
      } else {
        break;
      }
    }
    if (trimmedMain) {
      return `${trimmedMain}${brandSuffix}`;
    }
  }

  // Fallback: word-by-word truncation
  const words = cleanTitle.split(" ");
  let result = "";
  for (const word of words) {
    const candidate = result ? `${result} ${word}` : word;
    if (getPixelWidth(candidate, 20) <= maxPx) {
      result = candidate;
    } else {
      break;
    }
  }
  return result || cleanTitle.slice(0, 45);
}

/**
 * Ensure a description fits strictly within maxPx (default 970px).
 * Uses Arial 14px font size.
 */
export function ensureDescriptionLimit(desc, maxPx = 970) {
  if (!desc || typeof desc !== "string") return "";
  const cleanDesc = desc.trim().replace(/\s+/g, " ");
  const currentPx = getPixelWidth(cleanDesc, 14);
  if (currentPx <= maxPx) return cleanDesc;

  // Truncate at sentence boundary if possible, else word boundary
  const sentences = cleanDesc.match(/[^.!?]+[.!?]+/g) || [cleanDesc];
  let result = "";
  for (const sentence of sentences) {
    const candidate = result ? `${result} ${sentence.trim()}` : sentence.trim();
    if (getPixelWidth(candidate, 14) <= maxPx) {
      result = candidate;
    } else {
      break;
    }
  }

  if (result) return result;

  // If even the first sentence is too long, truncate word-by-word with ellipsis
  const words = cleanDesc.split(" ");
  for (const word of words) {
    const candidate = result ? `${result} ${word}` : word;
    // Leave room for ellipsis
    if (getPixelWidth(`${candidate}...`, 14) <= maxPx) {
      result = candidate;
    } else {
      break;
    }
  }
  return result ? `${result}...` : cleanDesc.slice(0, 140);
}
